// src/api/api.js
import axios from "axios";
import dayjs from "dayjs";
import config from "../config/config";
import { getCurrentBranchId, getCurrentClientId } from "../utils/auth";

// Create an axios instance
const api = axios.create({
    baseURL: config.API_BASE_URL, // base API URL from config
    headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
    },
    timeout: 30000, // 30 seconds timeout for mobile networks
});

// Function to get waiting customer counts
export const getWaitingCustomerCount = async (date) => {
    try {
        const formattedDate = dayjs(date).format("YYYY-MM-DD");
        const branchId = getCurrentBranchId();
        const response = await api.get(
            `/current_waiting_customer_count?date=${formattedDate}&branch_id=${branchId}`
        );
        return response.data;
    } catch (error) {
        console.error("API Error:", error);
        throw error;
    }
};

// ✅ Get all services for a branch
export const getAllServices = async (branchId) => {
    try {
        console.log("API: getAllServices called with branchId:", branchId);
        const response = await api.get(`/all-services/${branchId}`);
        console.log("API: getAllServices response:", response.data);
        return response.data;
    } catch (error) {
        console.error("API Error (getAllServices):", error);
        throw error;
    }
};

// ✅ Login with username and password
export const login = async (credentials) => {
    try {
        const response = await api.post("/login", credentials);
        return response;
    } catch (error) {
        console.error("API Error (login):", error);
        throw error;
    }
};

// ✅ Verify mobile login
export const verifyMobileLogin = async (user) => {
    try {
        const response = await api.post("/verify-mobile-login", { user });
        return response.data;
    } catch (error) {
        console.error("API Error (verifyMobileLogin):", error);
        throw error;
    }
};

// ✅ Fetch queues for a branch
export const getQueues = async (branchId) => {
    try {
        const response = await api.get(`/queues/${branchId}`);
        console.log("API Response - Queues:", response.data);
        return response.data;
    } catch (error) {
        console.error("API Error (getQueues):", error);
        throw error;
    }
};

// ✅ Fetch a specific queue by ID
export const getQueue = async (queueId) => {
    try {
        const response = await api.get(`/queue/${queueId}`);
        console.log("API Response - Queue:", response.data);
        return response.data;
    } catch (error) {
        console.error("API Error (getQueue):", error);
        throw error;
    }
};

// ✅ Check queue status by queue number and branch
export const checkQueueStatus = async (queueNumber, branchId) => {
    try {
        const response = await api.get(
            `/queue-status/${queueNumber}/${branchId}`
        );
        console.log("API Response - Queue Status:", response.data);
        return response.data;
    } catch (error) {
        console.error("API Error (checkQueueStatus):", error);
        throw error;
    }
};

// ✅ Update queue status (assist/done)
export const updateQueueStatus = async (queueId, branchId, updateStatus) => {
    try {
        const response = await api.post(`/queues/${queueId}/assist`, {
            branch_id: branchId, // Changed from client_id to branch_id
            update_status: updateStatus,
        });
        return response.data;
    } catch (error) {
        console.error("API Error (updateQueueStatus):", error);
        throw error;
    }
};

export const getTicket = async (code, retries = 2) => {
    try {
        // Validate code parameter
        if (!code || typeof code !== 'string' || code.trim() === '') {
            console.error('❌ getTicket: Invalid code parameter:', code);
            throw new Error('Invalid ticket code');
        }

        // Encode the code to handle special characters and ensure proper URL encoding
        const encodedCode = encodeURIComponent(code.trim());
        console.log('📞 getTicket API call - Original code:', code, 'Encoded:', encodedCode);
        
        // For DEMO mode, send branch_id if user is logged in (to show logged-in client's logo)
        let url = `/getticket/${encodedCode}`;
        if (code.toUpperCase() === 'DEMO') {
            const branchId = getCurrentBranchId();
            if (branchId) {
                url += `?branch_id=${branchId}`;
                console.log('📞 getTicket DEMO - Sending branch_id:', branchId);
            }
        }
        
        const response = await api.get(url);
        console.log('✅ getTicket API response:', response.data);
        return response.data;
    } catch (error) {
        console.error("❌ Error fetching ticket:", error);
        console.error("❌ Error details:", {
            code: code,
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
            url: error.config?.url
        });

        // Retry on network errors or timeouts for mobile devices
        if (retries > 0 && (
            error.code === 'ECONNABORTED' ||
            error.code === 'ERR_NETWORK' ||
            error.message?.includes('timeout') ||
            error.message?.includes('Network Error')
        )) {
            console.log(`🔄 Retrying getTicket... (${retries} retries left)`);
            // Wait 1 second before retrying
            await new Promise(resolve => setTimeout(resolve, 1000));
            return getTicket(code, retries - 1);
        }

        throw error;
    }
};

export const createQueueWithServices = async (ticketCode, serviceNames, queueRegTime = null) => {
    try {
        const branchId = getCurrentBranchId();
        const requestData = {
            ticket_code: ticketCode,
            service_names: serviceNames,
            branch_id: branchId,
        };

        // Add queue_reg_time if provided
        if (queueRegTime) {
            requestData.queue_reg_time = queueRegTime;
        }

        const response = await api.post(`/create-queue-with-services`, requestData);
        console.log(
            "API Response - Create Queue with Services:",
            response.data
        );
        return response.data;
    } catch (error) {
        console.error("API Error (createQueueWithServices):", error);
        throw error;
    }
};

export const addQueue = async (branchId, queueRegTime = null) => {
    try {
        const requestData = {};

        // Add queue_reg_time if provided
        if (queueRegTime) {
            requestData.queue_reg_time = queueRegTime;
        }

        const response = await api.post(`/add-queue/${branchId}`, requestData);
        return response.data; // adjust if your API wraps the data
    } catch (err) {
        console.error("API error adding queue:", err);
        throw err;
    }
};

export const addDirectAssistQueue = async (branchId, queueRegTime = null) => {
    try {
        const requestData = {};

        // Add queue_reg_time if provided
        if (queueRegTime) {
            requestData.queue_reg_time = queueRegTime;
        }

        const response = await api.post(`/add-direct-assist-queue/${branchId}`, requestData);
        return response.data;
    } catch (err) {
        console.error("API error adding direct assist queue:", err);
        throw err;
    }
};

// Reports API functions
export const getAverageCustomerCount = async (startDate, endDate) => {
    try {
        const branchId = getCurrentBranchId();
        const response = await api.get("/reports/average-customer-count", {
            params: {
                start_date: startDate,
                end_date: endDate,
                branch_id: branchId
            },
        });
        return response.data;
    } catch (error) {
        console.error("API Error (getAverageCustomerCount):", error);
        throw error;
    }
};

export const getCustomerHourReport = async (startDate, endDate) => {
    try {
        const branchId = getCurrentBranchId();
        const response = await api.get("/reports/customer-hour-report", {
            params: {
                start_date: startDate,
                end_date: endDate,
                branch_id: branchId
            },
        });
        return response.data;
    } catch (error) {
        console.error("API Error (getCustomerHourReport):", error);
        throw error;
    }
};

export const getAverageWaitingTime = async (startDate, endDate) => {
    try {
        const branchId = getCurrentBranchId();
        const response = await api.get("/reports/average-waiting-time", {
            params: {
                start_date: startDate,
                end_date: endDate,
                branch_id: branchId
            },
        });
        return response.data;
    } catch (error) {
        console.error("API Error (getAverageWaitingTime):", error);
        throw error;
    }
};

export const getQueueStatistics = async (date) => {
    try {
        const branchId = getCurrentBranchId();
        const response = await api.get("/reports/queue-statistics", {
            params: { date, branch_id: branchId },
        });
        return response.data;
    } catch (error) {
        console.error("API Error (getQueueStatistics):", error);
        throw error;
    }
};

export const getDetailedQueueReport = async (startDate, endDate) => {
    try {
        const branchId = getCurrentBranchId();
        const response = await api.get("/reports/detailed-queue-report", {
            params: {
                start_date: startDate,
                end_date: endDate,
                branch_id: branchId
            },
        });
        return response.data;
    } catch (error) {
        console.error("API Error (getDetailedQueueReport):", error);
        throw error;
    }
};

export const getLostCustomerStatistics = async (startDate, endDate) => {
    try {
        const branchId = getCurrentBranchId();
        const response = await api.get("/reports/lost-customer-statistics", {
            params: {
                start_date: startDate,
                end_date: endDate,
                branch_id: branchId
            },
        });
        return response.data;
    } catch (error) {
        console.error("API Error (getLostCustomerStatistics):", error);
        throw error;
    }
};

// Ad Management API functions
export const getAds = async () => {
    try {
        const branchId = getCurrentBranchId();
        const response = await api.get("/ads", {
            params: { branch_id: branchId },
        });
        return response.data;
    } catch (error) {
        console.error("API Error (getAds):", error);
        throw error;
    }
};

export const getFirstBranchForClient = async (clientId) => {
    try {
        const response = await api.get("/ads/first-branch", {
            params: { client_id: clientId },
        });
        console.log("First Branch API Response:", response.data);
        return {
            success: true,
            data: response.data.data || null,
        };
    } catch (error) {
        console.error("API Error (getFirstBranchForClient):", error);
        return {
            success: false,
            data: null,
            message:
                error.response?.data?.message || "Failed to get first branch",
        };
    }
};

export const getActiveAds = async (
    imageType = "landscape",
    clientId = null,
    branchId = null
) => {
    try {
        const defaultBranchId = getCurrentBranchId();
        const params = { image_type: imageType };

        // Handle different parameter combinations
        if (clientId && branchId) {
            // Both client_id and branch_id provided
            params.client_id = clientId;
            params.branch_id = branchId;
        } else if (clientId) {
            // Only client_id provided
            params.client_id = clientId;
        } else if (branchId) {
            // Only branch_id provided
            params.branch_id = branchId;
        } else {
            // Use default branch_id from localStorage
            params.branch_id = defaultBranchId;
        }

        const response = await api.get("/ads/active", { params });
        console.log("API Response:", response.data);
        // Return the full response structure that components expect
        return {
            success: true,
            data: response.data.data || [],
        };
    } catch (error) {
        console.error("API Error (getActiveAds):", error);
        return {
            success: false,
            data: [],
        };
    }
};

export const createAd = async (adData) => {
    try {
        const branchId = getCurrentBranchId();
        const formData = new FormData();

        // Append all fields to FormData
        Object.keys(adData).forEach((key) => {
            if (key === "image" && adData[key]) {
                formData.append(key, adData[key]);
            } else if (adData[key] !== null && adData[key] !== undefined) {
                formData.append(key, adData[key]);
            }
        });

        formData.append("branch_id", branchId);

        const response = await api.post("/ads", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        return {
            success: true,
            data: response.data,
        };
    } catch (error) {
        console.error("API Error (createAd):", error);
        return {
            success: false,
            message: error.response?.data?.message || "Failed to create ad",
        };
    }
};

export const updateAd = async (id, adData) => {
    try {
        const branchId = getCurrentBranchId();
        const formData = new FormData();

        // Append all fields to FormData
        Object.keys(adData).forEach((key) => {
            if (key === "image" && adData[key]) {
                formData.append(key, adData[key]);
            } else if (adData[key] !== null && adData[key] !== undefined) {
                formData.append(key, adData[key]);
            }
        });

        formData.append("branch_id", branchId);

        const response = await api.put(`/ads/${id}`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        return {
            success: true,
            data: response.data,
        };
    } catch (error) {
        console.error("API Error (updateAd):", error);
        return {
            success: false,
            message: error.response?.data?.message || "Failed to update ad",
        };
    }
};

export const deleteAd = async (id) => {
    try {
        const response = await api.delete(`/ads/${id}`);
        return response.data;
    } catch (error) {
        console.error("API Error (deleteAd):", error);
        throw error;
    }
};

export const updateAdSortOrder = async (ads) => {
    try {
        const response = await api.post("/ads/sort-order", { ads });
        return response.data;
    } catch (error) {
        console.error("API Error (updateAdSortOrder):", error);
        throw error;
    }
};

// Service Management API functions
export const getServices = async (branchId) => {
    try {
        const response = await api.get(`/services/${branchId}`);
        return response.data;
    } catch (error) {
        console.error("API Error (getServices):", error);
        throw error;
    }
};

export const createService = async (serviceData) => {
    try {
        const response = await api.post("/services", serviceData);
        return response.data;
    } catch (error) {
        console.error("API Error (createService):", error);
        // Return error response if available
        if (error.response?.data) {
            throw error.response.data;
        }
        throw error;
    }
};

export const updateService = async (id, serviceData) => {
    try {
        const response = await api.put(`/services/${id}`, serviceData);
        return response.data;
    } catch (error) {
        console.error("API Error (updateService):", error);
        // Return error response if available
        if (error.response?.data) {
            throw error.response.data;
        }
        throw error;
    }
};

export const deleteService = async (id) => {
    try {
        const response = await api.delete(`/services/${id}`);
        return response.data;
    } catch (error) {
        console.error("API Error (deleteService):", error);
        throw error;
    }
};

export const updateServiceSortOrder = async (services) => {
    try {
        const response = await api.post("/services/sort-order", { services });
        return response.data;
    } catch (error) {
        console.error("API Error (updateServiceSortOrder):", error);
        // Return error response if available
        if (error.response?.data) {
            throw error.response.data;
        }
        throw error;
    }
};

export const getServiceSettings = async (branchId) => {
    try {
        const response = await api.get(`/services/settings/${branchId}`);
        return response.data;
    } catch (error) {
        console.error("API Error (getServiceSettings):", error);
        throw error;
    }
};

export const updateServiceSettings = async (branchId, settings) => {
    try {
        const response = await api.post(`/services/settings/${branchId}`, settings);
        return response.data;
    } catch (error) {
        console.error("API Error (updateServiceSettings):", error);
        throw error;
    }
};

// Menu Management API functions
export const getMenus = async (branchId) => {
    try {
        const response = await api.get(`/menus/${branchId}`);
        return response.data;
    } catch (error) {
        console.error("API Error (getMenus):", error);
        throw error;
    }
};

export const createMenu = async (menuData) => {
    try {
        const response = await api.post("/menus", menuData);
        return response.data;
    } catch (error) {
        console.error("API Error (createMenu):", error);
        // Return error response if available
        if (error.response?.data) {
            throw error.response.data;
        }
        throw error;
    }
};

export const updateMenu = async (id, menuData) => {
    try {
        const response = await api.put(`/menus/${id}`, menuData);
        return response.data;
    } catch (error) {
        console.error("API Error (updateMenu):", error);
        // Return error response if available
        if (error.response?.data) {
            throw error.response.data;
        }
        throw error;
    }
};

export const deleteMenu = async (id) => {
    try {
        const response = await api.delete(`/menus/${id}`);
        return response.data;
    } catch (error) {
        console.error("API Error (deleteMenu):", error);
        throw error;
    }
};

export const updateMenuSortOrder = async (menus) => {
    try {
        const response = await api.post("/menus/sort-order", { menus });
        return response.data;
    } catch (error) {
        console.error("API Error (updateMenuSortOrder):", error);
        // Return error response if available
        if (error.response?.data) {
            throw error.response.data;
        }
        throw error;
    }
};

export const bulkUploadAds = async (adsData) => {
    try {
        const results = [];
        const errors = [];

        // Upload each ad individually using the existing createAd endpoint
        for (let i = 0; i < adsData.length; i++) {
            try {
                const adData = adsData[i];
                const response = await createAd(adData);

                if (response.success) {
                    results.push(response.data);
                } else {
                    errors.push(
                        `Ad ${i + 1}: ${response.message || "Failed to upload"}`
                    );
                }
            } catch (error) {
                errors.push(`Ad ${i + 1}: ${error.message || "Upload failed"}`);
            }
        }

        if (results.length === 0) {
            return {
                success: false,
                message:
                    errors.length > 0
                        ? errors.join("; ")
                        : "Failed to upload any ads",
            };
        }

        return {
            success: true,
            data: results,
            message:
                errors.length > 0
                    ? `Uploaded ${results.length
                    } ads successfully. Errors: ${errors.join("; ")}`
                    : `Successfully uploaded ${results.length} ads`,
        };
    } catch (error) {
        console.error("API Error (bulkUploadAds):", error);
        return {
            success: false,
            message: error.message || "Failed to upload ads",
        };
    }
};

// QR Code Management API functions
export const getQRCodes = async (branchId) => {
    try {
        const response = await api.get(`/qr-codes?branch_id=${branchId}`);
        return response.data;
    } catch (error) {
        console.error("API Error (getQRCodes):", error);
        throw error;
    }
};

export const createQRCode = async (qrData) => {
    try {
        const response = await api.post("/qr-codes", qrData);
        return response.data;
    } catch (error) {
        console.error("API Error (createQRCode):", error);
        throw error;
    }
};

export const updateQRCode = async (id, qrData) => {
    try {
        const response = await api.put(`/qr-codes/${id}`, qrData);
        return response.data;
    } catch (error) {
        console.error("API Error (updateQRCode):", error);
        throw error;
    }
};

export const deleteQRCode = async (id) => {
    try {
        const response = await api.delete(`/qr-codes/${id}`);
        return response.data;
    } catch (error) {
        console.error("API Error (deleteQRCode):", error);
        throw error;
    }
};

// SMS Queue Management API functions
export const validatePhoneNumber = async (phoneNumber, branchId) => {
    try {
        const response = await api.post("/sms/validate-phone", {
            phone_number: phoneNumber,
            branch_id: branchId,
        });
        return response.data;
    } catch (error) {
        console.error("API Error (validatePhoneNumber):", error);
        throw error;
    }
};

export const createSmsQueue = async (phoneNumber, branchId, serviceIds, queueRegTime = null) => {
    try {
        const requestData = {
            phone_number: phoneNumber,
            branch_id: branchId,
            service_ids: serviceIds,
        };

        // Add queue_reg_time if provided
        if (queueRegTime) {
            requestData.queue_reg_time = queueRegTime;
        }

        const response = await api.post("/sms/create-queue", requestData);
        return response.data;
    } catch (error) {
        console.error("API Error (createSmsQueue):", error);
        throw error;
    }
};

export const getCustomerInfo = async (phoneNumber) => {
    try {
        const response = await api.get("/sms/customer-info", {
            params: { phone_number: phoneNumber },
        });
        return response.data;
    } catch (error) {
        console.error("API Error (getCustomerInfo):", error);
        throw error;
    }
};
